import { confirmUnsavedChanges } from '@/shared/lib/confirmUnsavedChanges';
import { useUnsavedForm } from '@/shared/hooks/useUnsavedForm';
import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider } from 'react-hook-form';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import type { ApiOrderTypeSimpleModel } from '@/core/generated/api';
import { Link } from '@/shared/ui/Link/Link';
import { useOrders } from '@/entities/Order/model/store';
import {
    CREATE_PAYMENT_FORM_DEFAULTS,
    createPaymentSchema,
    type CreatePaymentFormValues,
} from '@/entities/Payment/model/createPaymentSchema';
import { Modal } from '@/shared/ui/Modal/Modal';
import { RequestError, RequestLoading } from '@/shared/ui/RequestState';
import { createRHFFields } from '@/shared/ui/RHFFields';
import { useCreatePaymentSubmit } from './useCreatePaymentSubmit';
import classes from './CreatePaymentModal.module.scss';

export interface CreatePaymentModalProps {
    visible: boolean;
    onHide: () => void;
    loading?: boolean;
    orderNumber: string;
    orderId: string;
    orderType: ApiOrderTypeSimpleModel;
}

const {
    TextField: PromoCodeField,
    CheckboxField: CustomServiceField,
    TextField: CustomServiceNameField,
    NumberField: CustomServicePriceField,
    NumberField: CustomServiceRewardField,
} = createRHFFields<CreatePaymentFormValues>();

/** Показывает форму создания платежа и результат оплаты заказа. */
export const CreatePaymentModal = (
    {
        orderId,
        visible,
        onHide,
        loading = false,
        orderNumber,
    }: CreatePaymentModalProps
) => {
    const form = useUnsavedForm<CreatePaymentFormValues>({
        resolver: zodResolver(createPaymentSchema),
        defaultValues: CREATE_PAYMENT_FORM_DEFAULTS,
    }, visible);

    const { onSubmit, paymentId, result, resetResult } = useCreatePaymentSubmit(orderId, form);
    const getOrderById = useOrders((state) => state.getOrderById);
    const hasCustomService = form.watch('hasCustomService');
    const { reset } = form;

    useEffect(() => {
        reset(CREATE_PAYMENT_FORM_DEFAULTS);
        resetResult();
    }, [orderId, reset, resetResult]);

    /** Закрывает окно и обновляет заказ после создания платежа. */
    const handleHide = () => confirmUnsavedChanges(form.formState.isDirty, () => {
        reset();

        if (paymentId) {
            void getOrderById(orderId);
        } else {
            reset(CREATE_PAYMENT_FORM_DEFAULTS);
            resetResult();
        }
        onHide();
    });

    const isSubmitting = form.formState.isSubmitting;
    const isPending = loading || isSubmitting;

    return <Modal
        visible={visible}
        onHide={handleHide}
        title={`Сформировать платеж и ссылку на оплату для заказа №${orderNumber}`}
        size="xl"
        className={classes.modal}
        closable={!isPending}
        footer={<div className={classes.footer}>
            <Button
                type="button"
                label={result
                    ? 'Закрыть'
                    : 'Отмена'
                }
                text
                onClick={handleHide}
                disabled={isPending}
            />
            {!result &&
                <Button
                    type="submit"
                    form="create-payment-form"
                    label={paymentId
                        ? 'Повторить получение ссылки'
                        : 'Сформировать платеж'
                    }
                    icon="pi pi-credit-card"
                    loading={isSubmitting}
                    disabled={isPending || !orderId}
                />
            }
        </div>}
    >
        {loading &&
            <RequestLoading message="Загрузка заказа..." />
        }
        {!loading && result?.kind === 'link' &&
            <div className={classes.result}>
                <Message
                    severity="success"
                    text={<span>
                        Ссылка на оплату для клиента: <Link
                            isBlank
                            url={result.url}
                            text="Оплатить на Robokassa"
                            showCopyButton={false}
                        />. Не забудьте продублировать её клиенту
                    </span>}
                />
                <Message
                    severity="info"
                    text="Платеж успешно сформирован, клиент может оплачивать заказ"
                />
            </div>
        }
        {!loading && result?.kind === 'subscription' &&
            <Message
                severity="success"
                text="Заказ успешно оплачен абонементом клиента. Оплата не требуется, сообщите об этом клиенту"
            />
        }
        {!loading && !result &&
            <FormProvider {...form}>
                <form
                    id="create-payment-form"
                    onSubmit={form.handleSubmit(onSubmit)}
                    className={classes.form}
                    noValidate
                >
                    {!paymentId &&
                        <>
                            <PromoCodeField
                                name="promoCodeNumber"
                                label="Промокод"
                                placeholder="Введите промокод если есть"
                                disabled={isPending}
                            />
                            <CustomServiceField
                                name="hasCustomService"
                                label="Добавить дополнительную услугу"
                                disabled={isPending}
                            />
                            {hasCustomService &&
                                <div className={classes.customService}>
                                    <h3 className={classes.sectionTitle}>Дополнительная услуга</h3>
                                    <CustomServiceNameField
                                        name="customServiceName"
                                        label="Наименование услуги"
                                        placeholder="Введите наименование услуги"
                                        disabled={isPending}
                                        isRequired
                                    />
                                    <CustomServicePriceField
                                        name="customServicePrice"
                                        label="Цена (руб)"
                                        placeholder="Введите цену услуги"
                                        mode="currency"
                                        currency="RUB"
                                        min={0}
                                        disabled={isPending}
                                        isRequired
                                    />
                                    <CustomServiceRewardField
                                        name="customServiceExecutorReward"
                                        label="Вознаграждение исполнителя (руб)"
                                        placeholder="Введите вознаграждение исполнителя"
                                        mode="currency"
                                        currency="RUB"
                                        min={0}
                                        disabled={isPending}
                                        isRequired
                                    />
                                </div>
                            }
                        </>
                    }
                    {paymentId &&
                        <Message
                            severity="info"
                            text="Платеж создан. Повторите получение ссылки на оплату"
                        />
                    }
                    {isSubmitting &&
                        <RequestLoading message="Формируем платеж..." />
                    }
                    {form.formState.errors.root?.server?.message &&
                        <RequestError
                            message={form.formState.errors.root.server.message}
                            compact
                        />
                    }
                </form>
            </FormProvider>
        }
    </Modal>;
};
